import { type CSSProperties } from 'react';

/**
 * Node layout engine for the edit canvas.
 *
 * Data flow between functions:
 *
 *   toNodeRect ──────────────────────────► NodeRect[]
 *                                               │
 *            ┌──────────────────────────────────┘
 *            │
 *            ▼
 *   computeRowAwareAppendPosition
 *     └─ groupNodesIntoRows
 *
 *   computeGridLayout (standalone, for "tidy up" re-layout)
 *
 *   rectsOverlap / findOverlappingNodes (standalone overlap detection)
 */

/** Simplified rectangle for layout math, decoupled from React Flow's Node type */
export type NodeRect = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Width and height dimensions */
export type Size = { width: number; height: number };

/** A displacement instruction: move node `id` to new position */
export type NodeDisplacement = {
  id: string;
  position: { x: number; y: number };
};

/** Result of computing insertion position + any displacements */
export type InsertionResult = {
  position: { x: number; y: number };
  displacements: NodeDisplacement[];
};

const DEFAULT_NODE_SIZE = 200;

/**
 * Extracts a {@link NodeRect} from a React Flow node. Prefers `measured`
 * (DOM-accurate, updated by ReactFlow's ResizeObserver) over `style`
 * (set by our code, can lag behind after resize) over `width`/`height`
 * (set by applyNodeChanges).
 */
export function toNodeRect(node: {
  id: string;
  position: { x: number; y: number };
  measured?: { width?: number; height?: number };
  style?: Pick<CSSProperties, 'width' | 'height'>;
  width?: number;
  height?: number;
}): NodeRect {
  return {
    height:
      node.measured?.height ??
      (typeof node.style?.height === 'number' ? node.style.height : undefined) ??
      node.height ??
      DEFAULT_NODE_SIZE,
    id: node.id,
    width:
      node.measured?.width ??
      (typeof node.style?.width === 'number' ? node.style.width : undefined) ??
      node.width ??
      DEFAULT_NODE_SIZE,
    x: node.position.x,
    y: node.position.y,
  };
}

/**
 * Check whether two rectangles overlap (strict — touching edges are not overlapping).
 */
export function rectsOverlap({ a, b }: { a: NodeRect; b: NodeRect }): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

/**
 * Find all nodes that overlap with a given rectangle.
 * Returns the IDs of overlapping nodes (excludes the target itself).
 */
export function findOverlappingNodes({ target, allNodes }: { target: NodeRect; allNodes: NodeRect[] }): string[] {
  return allNodes
    .filter((node) => node.id !== target.id && rectsOverlap({ a: target, b: node }))
    .map((node) => node.id);
}

/**
 * Groups nodes into rows by merging vertically overlapping extents.
 * Users can freely drag nodes, so we can't rely on exact y-alignment —
 * instead, nodes whose vertical ranges overlap are considered the same row.
 */
function groupNodesIntoRows(nodes: NodeRect[]): NodeRect[][] {
  if (nodes.length === 0) {
    return [];
  }

  const sorted = [...nodes].sort((a, b) => a.y - b.y || a.x - b.x);
  const rows: NodeRect[][] = [];
  let currentRow: NodeRect[] = [sorted[0]];
  let rowBottom = sorted[0].y + sorted[0].height;

  for (let i = 1; i < sorted.length; i++) {
    const node = sorted[i];

    if (node.y < rowBottom) {
      currentRow.push(node);
      // Extend the row's vertical range so a tall node pulls in shorter neighbors
      rowBottom = Math.max(rowBottom, node.y + node.height);
    } else {
      rows.push(currentRow);
      currentRow = [node];
      rowBottom = node.y + node.height;
    }
  }
  rows.push(currentRow);

  // Sort each row left-to-right for consistent insertion/displacement sweep order
  for (const row of rows) {
    row.sort((a, b) => a.x - b.x);
  }

  return rows;
}

/**
 * Appends a node to the canvas, placing it to the right of the target row.
 * Wraps to a new row if appending would exceed maxRowWidth.
 *
 * @param options.newNodeSize - Dimensions of the new node
 * @param options.allNodes - All current node rects on the canvas
 * @param options.maxRowWidth - Maximum row width before wrapping to a new row
 * @param options.gap - Horizontal spacing between nodes
 * @param options.targetRowNodeId - Append to the row containing this node. Falls back to the bottom-most row.
 */
export function computeRowAwareAppendPosition({
  newNodeSize,
  allNodes,
  maxRowWidth,
  gap,
  targetRowNodeId,
}: {
  newNodeSize: Size;
  allNodes: NodeRect[];
  maxRowWidth: number;
  gap: number;
  targetRowNodeId?: string;
}): InsertionResult {
  if (allNodes.length === 0) {
    return { displacements: [], position: { x: 0, y: 0 } };
  }

  const rows = groupNodesIntoRows(allNodes);
  const targetRow = targetRowNodeId
    ? (rows.find((row) => row.some((n) => n.id === targetRowNodeId)) ?? rows[rows.length - 1])
    : rows[rows.length - 1];

  const rowLeft = Math.min(...targetRow.map((n) => n.x));
  const rowRight = Math.max(...targetRow.map((n) => n.x + n.width));
  const currentRowWidth = rowRight - rowLeft;
  const projectedRowWidth = currentRowWidth + gap + newNodeSize.width;

  if (projectedRowWidth > maxRowWidth && targetRow.length > 1) {
    const allBottom = allNodes.reduce((max, n) => Math.max(max, n.y + n.height), 0);

    return {
      displacements: [],
      position: { x: rowLeft, y: allBottom + gap },
    };
  }

  return {
    displacements: [],
    position: {
      x: rowRight + gap,
      y: targetRow[0].y,
    },
  };
}

/**
 * Computes a full grid re-layout, intended for a future "tidy up" action.
 * Arranges nodes left-to-right in rows, preserving array order.
 *
 * @param options.nodes - Nodes to lay out (order is preserved)
 * @param options.maxRowWidth - Maximum width before wrapping to the next row
 * @param options.gap - Spacing between nodes (both horizontal and vertical)
 */
export function computeGridLayout({
  nodes,
  maxRowWidth,
  gap,
}: {
  nodes: NodeRect[];
  maxRowWidth: number;
  gap: number;
}): NodeDisplacement[] {
  if (nodes.length === 0) {
    return [];
  }

  const displacements: NodeDisplacement[] = [];
  let currentX = 0;
  let currentY = 0;
  let rowMaxHeight = 0;

  for (const node of nodes) {
    if (currentX > 0 && currentX + node.width > maxRowWidth) {
      currentY += rowMaxHeight + gap;
      currentX = 0;
      rowMaxHeight = 0;
    }

    displacements.push({
      id: node.id,
      position: { x: currentX, y: currentY },
    });

    currentX += node.width + gap;
    rowMaxHeight = Math.max(rowMaxHeight, node.height);
  }

  return displacements;
}
